/**
 * Схема шкафа — вид спереди.
 *
 * Как настоящий шкаф: слева и справа стойки с отверстиями, номера
 * юнитов на левой стойке, сверху вниз. Оборудование нарисовано лицевой
 * панелью своего типа: у коммутатора — порты с индикаторами, у
 * патч-панели — гнёзда, у органайзера — прорези для кабеля, у ИБП —
 * экран и решётка. Справа на корпусе — табличка с названием.
 *
 * Оборудование высотой в несколько юнитов — один блок. Пустой юнит
 * показывает стойку изнутри; щелчок по любому юниту открывает правку.
 */
import { buildRackLayout } from '../../domain/racks';
import styles from './RackDiagram.module.css';

/** Высота одного юнита на схеме, px. */
const UNIT_HEIGHT = 34;

/**
 * Вид корпуса. Тип из учёта (rackItemType) уточняется по названию:
 * ИБП, сервер и полка в учёте относятся к «прочему», но нарисованы
 * по-своему — так их легче узнать на схеме.
 */
function visualKind(type, name) {
  const n = String(name || '').toLowerCase();
  if (type !== 'other') return type;
  if (/ибп|ups/.test(n)) return 'ups';
  if (/сервер|server|видеорегистратор|nvr/.test(n)) return 'server';
  if (/полк/.test(n)) return 'shelf';
  return 'other';
}

/** Подпись на корпусе — в единственном числе: это одно устройство. */
const KIND_LABELS = {
  switch: 'Коммутатор',
  patch: 'Патч-панель',
  organizer: 'Органайзер',
  optic: 'Оптика',
  ups: 'ИБП',
  server: 'Сервер',
  shelf: 'Полка',
  other: 'Прочее',
};

/** Повторить элемент n раз. */
const times = (n, render) => Array.from({ length: n }, (_, i) => render(i));

/** Рисунок лицевой панели по типу. Только оформление — для чтения экрана скрыт. */
function Faceplate({ kind, span, seed }) {
  switch (kind) {
    case 'switch':
      return (
        <span className={styles.art}>
          <span className={styles.portGrid}>
            {times(24, (i) => (
              <span key={i} className={styles.rj45}>
                <i className={(i * 7 + seed) % 9 === 0 ? styles.ledOff : styles.ledOn} />
              </span>
            ))}
          </span>
          <span className={styles.sfps}>{times(2, (i) => <span key={i} className={styles.sfp} />)}</span>
        </span>
      );
    case 'patch':
      return (
        <span className={`${styles.art} ${styles.artPatch}`}>
          {times(24, (i) => <span key={i} className={styles.keystone} />)}
        </span>
      );
    case 'optic':
      return (
        <span className={`${styles.art} ${styles.artOptic}`}>
          {times(12, (i) => <span key={i} className={styles.lc}><i /><i /></span>)}
        </span>
      );
    case 'organizer':
      return (
        <span className={`${styles.art} ${styles.artOrganizer}`}>
          {times(5, (i) => <span key={i} className={styles.ring} />)}
        </span>
      );
    case 'ups':
      return (
        <span className={styles.art}>
          <span className={styles.lcd}>230 V</span>
          <span className={styles.buttons}>{times(3, (i) => <span key={i} />)}</span>
          <span className={styles.grille} />
        </span>
      );
    case 'server':
      return (
        <span className={styles.art}>
          <span className={styles.bays}>
            {times(span > 1 ? 8 : 4, (i) => <span key={i} className={styles.bay}><i /></span>)}
          </span>
          <span className={styles.power} />
        </span>
      );
    case 'shelf':
      return <span className={`${styles.art} ${styles.artShelf}`}><span className={styles.tray} /></span>;
    case 'reserve':
      return <span className={styles.art} />;
    default:
      return <span className={styles.art}><span className={styles.vents} /></span>;
  }
}

export default function RackDiagram({ height, items, onSelectUnit, onOpenSwitch }) {
  const blocks = buildRackLayout(height, items);

  return (
    <div className={styles.rack}>
      <div className={styles.cap} aria-hidden="true" />
      {blocks.map((block) => {
        const { unit, span, item, type } = block;
        const mismatch = item?.shkafMismatch;
        const kind = item ? visualKind(type, item.name) : 'empty';
        const units = span > 1 ? `${unit}–${unit - span + 1}` : String(unit);

        /* Подпись для программ чтения с экрана: рисунок и положение
           им недоступны, поэтому состояние проговаривается словами. */
        const label = item
          ? `Юнит ${units}: ${item.name}` +
            (KIND_LABELS[kind] ? `. ${KIND_LABELS[kind]}` : '') +
            (mismatch ? `. Расхождение: числится в шкафу ${item.actualShkaf}` : '')
          : `Юнит ${unit}: свободно`;

        const select = () => onSelectUnit(unit);

        return (
          <div
            key={unit}
            className={`${styles.row} ${item ? '' : styles.rowEmpty}`}
            style={{ height: span * UNIT_HEIGHT }}
          >
            <span className={styles.numbers} aria-hidden="true">
              {times(span, (i) => <span key={i}>{unit - i}</span>)}
            </span>
            <span className={styles.rail} aria-hidden="true" />

            <div
              className={[
                styles.device,
                styles[`kind_${kind}`] ?? '',
                mismatch ? styles.mismatch : '',
              ].filter(Boolean).join(' ')}
              onClick={select}
              role="button"
              tabIndex={0}
              aria-label={label}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  select();
                }
              }}
            >
              {item ? (
                <>
                  <span className={styles.ear} aria-hidden="true">{times(span, (i) => <i key={i} />)}</span>
                  <Faceplate kind={kind} span={span} seed={unit} />
                  <span className={styles.plate}>
                    <span className={styles.plateMain}>
                      <span className={styles.name}>{item.name}</span>
                      {item.comment && span > 1 && <span className={styles.comment}>{item.comment}</span>}
                    </span>
                    <span className={styles.tags}>
                      {mismatch && (
                        <span className={styles.badgeErr} title={`По учёту: ${item.actualShkaf}`}>
                          не тот шкаф
                        </span>
                      )}
                      {item.switchExists && (
                        <button
                          className={styles.open}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenSwitch(item.matchedSw);
                          }}
                          title="Открыть карточку коммутатора"
                          aria-label={`Открыть карточку ${item.matchedSw}`}
                        >
                          Карточка
                        </button>
                      )}
                      {kind !== 'reserve' && <span className={styles.badge}>{KIND_LABELS[kind] ?? ''}</span>}
                    </span>
                  </span>
                  <span className={styles.ear} aria-hidden="true">{times(span, (i) => <i key={i} />)}</span>
                </>
              ) : (
                <span className={styles.free}>свободно</span>
              )}
            </div>

            <span className={styles.rail} aria-hidden="true" />
          </div>
        );
      })}
      <div className={styles.cap} aria-hidden="true" />
    </div>
  );
}
